import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/api/client';

export default function DeliveryAddressCard({ address, onEdit }) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => api.entities.DeliveryAddress.delete(address.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveryAddresses'] });
      toast.success('Address removed');
    },
    onError: (error) => toast.error(error.message ?? 'Failed to remove address'),
  });

  const handleDelete = () => {
    // Safe to delete outright: shipped labels keep their own copy of the
    // address, so removing the book entry cannot change what was printed.
    if (window.confirm(`Remove "${address.name}" from the address book?`)) deleteMutation.mutate();
  };

  return (
    <Card className="border-slate-200 hover:border-green-300 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5 text-green-600" />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-slate-900 truncate">{address.name}</h4>
              <p className="text-sm text-slate-600">{address.address1}</p>
              {address.address2 && <p className="text-sm text-slate-600">{address.address2}</p>}
              <p className="text-sm text-slate-600">
                {address.city}, {address.state} {address.zip}
              </p>
            </div>
          </div>

          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(address)}
              className="hover:bg-green-50 hover:text-green-600"
              aria-label={`Edit ${address.name}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="hover:bg-red-50 hover:text-red-600"
              aria-label={`Remove ${address.name}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
